/*
 * @Copyright 2023-2026. Institute for Future Intelligence, Inc.
 */

import zhCN from 'antd/lib/locale/zh_CN';
import zhTW from 'antd/lib/locale/zh_TW';
import enUS from 'antd/lib/locale/en_US';

import React, { useMemo } from 'react';
import { useStore } from './stores/common';
import { usePrimitiveStore } from './stores/commonPrimitive';
import styled from 'styled-components';
import logo from './assets/aims-logo-32.png';
import * as Selector from './stores/selector';
import { Util } from './Util';
import { useTranslation } from 'react-i18next';
import { ensureLocale } from './i18n/i18n';
import ProjectMenu from './components/mainMenu/projectMenu';
import ViewMenu from './components/mainMenu/viewMenu';
import EditMenu from './components/mainMenu/editMenu';
import { Language } from './constants';
import AccessoriesMenu from './components/mainMenu/accessoriesMenu.tsx';
import { ClickEvent, Menu, MenuItem, MenuRadioGroup } from '@szhsin/react-menu';
import { MainMenuItem, MainSubMenu } from './components/menuItem.tsx';
import { MenuOutlined } from '@ant-design/icons';
import '@szhsin/react-menu/dist/index.css';
import '@szhsin/react-menu/dist/transitions/zoom.css';
import './components/mainMenu/style.css';

const MainMenuContainer = styled.div`
  width: 100px;
`;

const StyledImage = styled.img`
  position: absolute;
  top: 10px;
  left: 30px;
  height: 40px;
  transition: 0.5s;
  opacity: 1;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.5;
  }
`;

const LabelContainer = styled.div`
  position: absolute;
  top: 54px;
  left: 0;
  width: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;
  z-index: 9;
`;

const MainMenu = React.memo(({ viewOnly, isMobile = false }: { viewOnly: boolean; isMobile?: boolean }) => {
  const setCommonStore = useStore(Selector.set);
  const user = useStore(Selector.user);
  const language = useStore(Selector.language);

  const { t } = useTranslation();
  const lang = useMemo(() => {
    return { lng: language };
  }, [language]);

  const isMac = useMemo(() => Util.isMac(), []);

  const keyHome = useMemo(() => {
    const os = Util.getOS();
    if (os) {
      if (os.includes('OS X')) {
        return 'Ctrl+Alt+H';
      }
      if (os.includes('Chrome')) {
        return 'Ctrl+Alt+H';
      }
    }
    return 'Ctrl+Home';
  }, []);

  const desktopButton = () => (
    <MainMenuContainer>
      <StyledImage src={logo} title={t('tooltip.clickToOpenMenu', lang)} />
      <LabelContainer>
        <span style={{ fontSize: '10px', alignContent: 'center', cursor: 'pointer' }}>{t('menu.mainMenu', lang)}</span>
      </LabelContainer>
    </MainMenuContainer>
  );

  const mobileButton = () => (
    <span
      title={t('tooltip.clickToOpenMenu', lang)}
      style={{
        position: 'absolute',
        top: '6px',
        left: '8px',
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        zIndex: 20,
      }}
    >
      <MenuOutlined style={{ fontSize: '22px', color: '#222' }} />
    </span>
  );

  const menuButton = isMobile ? mobileButton : desktopButton;

  const onLanguageItemClick = (e: ClickEvent) => {
    e.keepOpen = true;
  };

  return (
    <>
      <Menu
        menuButton={menuButton}
        menuStyle={{ fontSize: '14px', minWidth: '4rem', borderRadius: '0.35rem' }}
        transition
      >
        {!viewOnly && user.uid && <ProjectMenu isMac={isMac} />}

        {<EditMenu isMac={isMac} />}

        <ViewMenu keyHome={keyHome} isMac={isMac} />

        <AccessoriesMenu />

        <MainSubMenu label={t('menu.languageSubMenu', lang)}>
          <MenuRadioGroup
            value={language}
            onRadioChange={(e) => {
              // See standaloneLanguageSelect: start the split translation
              // bundle loading before the store flips to the new language.
              ensureLocale(e.value);
              setCommonStore((state) => {
                state.language = e.value;
                switch (state.language) {
                  case 'zh_cn':
                    state.locale = zhCN;
                    break;
                  case 'zh_tw':
                    state.locale = zhTW;
                    break;
                  default:
                    state.locale = enUS;
                }
              });
            }}
          >
            <MenuItem type="radio" value={'en'} onClick={onLanguageItemClick}>
              {Language.English}
            </MenuItem>
            <MenuItem type="radio" value={'zh_cn'} onClick={onLanguageItemClick}>
              {Language.ChineseSimplified}
            </MenuItem>
            <MenuItem type="radio" value={'zh_tw'} onClick={onLanguageItemClick}>
              {Language.ChineseTraditional}
            </MenuItem>
          </MenuRadioGroup>
        </MainSubMenu>

        <MainSubMenu label={t('menu.helpSubMenu', lang)}>
          <MainMenuItem
            onClick={() => {
              // Re-show the landing in place; the user can pick a different
              // project type or hit Skip to return to whatever they had open.
              // Doesn't touch projectState — Skip drops them back exactly
              // where they were.
              usePrimitiveStore.getState().set((state) => {
                state.showLanding = true;
              });
            }}
          >
            {t('menu.Welcome', lang)}
          </MainMenuItem>

          <MainMenuItem
            onClick={() => {
              // Same left-edge drawer the toolbar lightbulb opens.
              usePrimitiveStore.getState().set((state) => {
                state.showExamplesDrawer = true;
              });
            }}
          >
            {t('menu.examplesSubMenu', lang)}
          </MainMenuItem>

          <MainMenuItem
            onClick={() => {
              const base = import.meta.env.BASE_URL || '/';
              window.open(base + 'commons', '_blank', 'noopener');
            }}
          >
            {t('menu.BrowseTheCommons', lang)}
          </MainMenuItem>

          <MainMenuItem
            onClick={() => {
              const base = import.meta.env.BASE_URL || '/';
              window.open(base + 'lesson-commons', '_blank', 'noopener');
            }}
          >
            {t('menu.BrowseLessonPlans', lang)}
          </MainMenuItem>

          <MainMenuItem
            onClick={() => {
              const base = import.meta.env.BASE_URL || '/';
              window.open(base + 'tutorial', '_blank', 'noopener');
            }}
          >
            {t('menu.OpenTutorial', lang)}
          </MainMenuItem>

          <MainMenuItem
            onClick={() => {
              const base = import.meta.env.BASE_URL || '/';
              window.open(base + 'case-studies', '_blank', 'noopener');
            }}
          >
            {t('menu.Ideas', lang)}
          </MainMenuItem>

          <MainMenuItem
            onClick={() => {
              const base = import.meta.env.BASE_URL || '/';
              window.open(base + 'terms', '_blank', 'noopener');
            }}
          >
            {t('aboutUs.TermsOfService', lang)}
          </MainMenuItem>

          <MainMenuItem
            onClick={() => {
              const base = import.meta.env.BASE_URL || '/';
              window.open(base + 'privacy', '_blank', 'noopener');
            }}
          >
            {t('aboutUs.PrivacyPolicy', lang)}
          </MainMenuItem>

          <MainMenuItem
            onClick={() => {
              const base = import.meta.env.BASE_URL || '/';
              window.open(base + 'about', '_blank', 'noopener');
            }}
          >
            {t('menu.AboutUs', lang)}
          </MainMenuItem>
        </MainSubMenu>
      </Menu>
    </>
  );
});

export default MainMenu;
